import { PcbNodeTypes, } from "@buildwithflux/models";
import { vec2 } from "../../common";
import { PcbNodeHelper } from "../../nodes";
import { getRouteSegmentAbsStartEndPos } from "../layout_engine/helpers/getRouteSegmentAbsStartEndPos";
// HACK: we have a dangerous while(true), so we constrain it to 10k iterations
// This means that the algorithm will fail with more than 5k unconnected components per net
const KRUSKAL_MAX_ITER = 5_000;
export const AirwireIdleOpacity = 0.3;
export const AirwireHighlightOpacity = 1;
export function createAirwireEdgeKey(nodeUids) {
    return nodeUids[0] < nodeUids[1] ? `${nodeUids[0]}:${nodeUids[1]}` : `${nodeUids[1]}:${nodeUids[0]}`;
}
/**
 * Calculates a set of ids and line positions to display airwires on the canvas.
 *
 * Returns:
 * - airwiresPositions is a linear array with the positions of every line segment [xa,ya,za, xb,yb,zb, opacity]
 * - airwiresGraphIds is a linear array with every start/end graph id [startId, endId]
 * - endpointsPerNet returns useful info about every possible endpoint, for calculating airwires live during routing
 */
export function calculateAirwires(netMap, connectedComponents, pcbLayoutNodes, { getNodePositionWorld, getNodeAttributes, log, }) {
    const endpointsPerNet = new Map();
    const airwireEdgeOpacityIndices = new Map();
    // We first augment the connected components with position
    const connectedComponentsWithInfo = connectedComponents.map((graphIds) => {
        const nodeIds = graphIds.map((graphId) => {
            return getNodeAttributes(graphId)?.nodeUid;
        });
        const positions = graphIds.map((graphId) => {
            const attr = getNodeAttributes(graphId);
            if (!attr) {
                return { x: 0, y: 0 };
            }
            // If the node is being dragged, we fix the original baked centerPos by using the live position
            const nodeId = attr.nodeUid;
            const node = pcbLayoutNodes[nodeId];
            const livePosition = getNodePositionWorld(nodeId);
            if (!node || node.type === PcbNodeTypes.root || !livePosition) {
                return attr.centerPos;
            }
            const bakedPosition = node.bakedRules?.positionRelativeToDocument;
            const positionOffset = vec2.sub(livePosition, bakedPosition);
            return vec2.add(attr.centerPos, positionOffset);
        });
        const noRoutings = graphIds.map((id) => {
            const attributes = getNodeAttributes(id);
            if (!attributes)
                return null;
            return attributes.noRouting;
        });
        return { graphIds, nodeIds, positions, noRoutings };
    });
    // For each logical net, find which physical connected components it maps to in the pcb connectivity graph
    const connectedComponentsPerNet = Object.entries(netMap).map(([netId, terminalsMap]) => {
        const terminalIds = Object.keys(terminalsMap);
        return {
            netId,
            connectedComponents: connectedComponentsWithInfo.filter((cc) => terminalIds.some((ti) => cc.nodeIds.includes(ti))),
        };
    });
    // A net with more than 1 physical connected components is not connected properly, so it's an airwire
    // If a net is under a top-level footprint, we dont create airwire for it even theres more than 1 dis-connected components
    const connectedComponentsPerNetThatNeedAirwires = connectedComponentsPerNet.filter((cc) => {
        if (cc.connectedComponents.length <= 1) {
            return false;
        }
        const netNode = getTypedBakedNodeFromMap(pcbLayoutNodes, cc.netId, PcbNodeTypes.net);
        const topLevelFootprintUid = netNode?.bakedRules.topLevelFootprintUid;
        if (topLevelFootprintUid) {
            const topLevelFootprintNode = getTypedBakedNodeFromMap(pcbLayoutNodes, topLevelFootprintUid, PcbNodeTypes.footprint);
            return !topLevelFootprintNode;
        }
        else {
            return true;
        }
    });
    const airwiresGraphIds = [];
    const airwiresNodeIds = [];
    const airwiresPositions = [];
    // Airwires are calculated with a kruskal-like MST algorithm
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    connectedComponentsPerNetThatNeedAirwires.forEach((netWithCC) => {
        const netNode = getTypedBakedNodeFromMap(pcbLayoutNodes, netWithCC.netId, PcbNodeTypes.net);
        // Used for airwires during routing
        const netEndpoints = netWithCC.connectedComponents.reduce((acc, cc, ccI) => {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            cc.graphIds.forEach((id, idI) => {
                if (!id)
                    return;
                const pcbNodeUid = cc.nodeIds[idI];
                const node = pcbLayoutNodes[pcbNodeUid];
                if (!(node && PcbNodeHelper.isCopperNodeType(node) && node.bakedRules.effectiveNetId === netNode?.uid))
                    return;
                const pos = cc.positions[idI];
                if (!pos) {
                    throw new Error(`Position for node ${pcbNodeUid} in net ${netWithCC.netId} is undefined.`);
                }
                if (node.type === PcbNodeTypes.routeSegment) {
                    const [startPos, endPos] = getRouteSegmentAbsStartEndPos(node, pcbLayoutNodes);
                    acc.push({
                        graphNodeUid: id,
                        pcbNodeUid: pcbNodeUid,
                        pos: startPos,
                        ccI,
                        degree: 0,
                        noRouting: cc.noRoutings[idI] ?? null,
                    });
                    acc.push({
                        graphNodeUid: id,
                        pcbNodeUid: pcbNodeUid,
                        pos: endPos,
                        ccI,
                        degree: 0,
                        noRouting: cc.noRoutings[idI] ?? null,
                    });
                }
                else {
                    acc.push({
                        graphNodeUid: id,
                        pcbNodeUid: pcbNodeUid,
                        pos,
                        ccI,
                        degree: 0,
                        noRouting: cc.noRoutings[idI] ?? null,
                    });
                }
            });
            return acc;
        }, []);
        endpointsPerNet.set(netWithCC.netId, netEndpoints);
        // Not enough point for airwires
        if (netEndpoints.length < 2) {
            return;
        }
        // We need to keep track which connected components are now connected to calculate an MST,
        // otherwise we could add the same edge twice and/or leave things disconnected
        // This is the magical "FIND-SET" they talk about in the magical computer science scrolls!
        const alreadyConnected = netWithCC.connectedComponents.map((_cc, i) => i);
        let iter = 0;
        while (iter < KRUSKAL_MAX_ITER) {
            // Exit condition: every component is in the same set => all the components are connected
            if (Math.min(...alreadyConnected) === Math.max(...alreadyConnected)) {
                break;
            }
            // We need to find the best point to connect our connected component to
            // AKA: we need to look into all the other points outside of it and find the closest one
            // TODO: O(N^2) algorithm, let's hope that N is small...
            let closestPointI_A = 0;
            let closestPointI_B = 0;
            let minDist = Infinity;
            for (let iA = 0; iA < netEndpoints.length; iA++) {
                const ACCSet = alreadyConnected[netEndpoints[iA].ccI];
                const AnoRouting = netEndpoints[iA].noRouting;
                // Some nodes are not routable to, so let's skip them
                if (AnoRouting)
                    continue;
                for (let iB = 0; iB < netEndpoints.length; iB++) {
                    const BCCSet = alreadyConnected[netEndpoints[iB].ccI];
                    // Same node or same connected component are not useful
                    if (ACCSet === BCCSet)
                        continue;
                    const BnoRouting = netEndpoints[iB].noRouting;
                    if (BnoRouting)
                        continue;
                    const posA = netEndpoints[iA].pos;
                    const posB = netEndpoints[iB].pos;
                    if (!posA || !posB)
                        continue;
                    const distance = vec2.distance(posA, posB);
                    if (distance > 0 && distance < minDist) {
                        minDist = distance;
                        closestPointI_A = iA;
                        closestPointI_B = iB;
                    }
                }
            }
            const closestPointA = netEndpoints[closestPointI_A];
            const closestPointB = netEndpoints[closestPointI_B];
            const ACCI = closestPointA.ccI;
            const BCCI = closestPointB.ccI;
            const APosI = closestPointA.pos;
            const BPosI = closestPointB.pos;
            if (APosI && BPosI) {
                // Once we add the airwire we merge the disjoint set for the two airwires
                const oldCCSet = alreadyConnected[ACCI];
                const newCCSet = alreadyConnected[BCCI];
                for (let j = 0; j < alreadyConnected.length; j++) {
                    if (alreadyConnected[j] === oldCCSet) {
                        alreadyConnected[j] = newCCSet;
                    }
                }
                const opacityParameterIndex = airwiresPositions.length + 6; // opacity is the last element in the segment
                airwiresGraphIds.push(closestPointA.graphNodeUid);
                airwiresGraphIds.push(closestPointB.graphNodeUid);
                airwiresNodeIds.push(closestPointA.pcbNodeUid);
                airwiresNodeIds.push(closestPointB.pcbNodeUid);
                airwiresPositions.push(APosI.x); // x
                airwiresPositions.push(APosI.y); // y
                airwiresPositions.push(0); // z
                airwiresPositions.push(BPosI.x); // x
                airwiresPositions.push(BPosI.y); // y
                airwiresPositions.push(0); // z
                airwiresPositions.push(AirwireIdleOpacity); // opacity
                // Used for highlighting when hovering the DRC item
                // TODO: this is a terrible representation for an airwire, since it's not always node to node
                airwireEdgeOpacityIndices.set(createAirwireEdgeKey([closestPointA.pcbNodeUid, closestPointB.pcbNodeUid]), opacityParameterIndex);
                // Update the airwires endpoint graph degree, used to avoid disconnecting stuff later
                netEndpoints[closestPointI_A].degree += 1;
                netEndpoints[closestPointI_B].degree += 1;
            }
            iter++;
        }
        if (iter >= KRUSKAL_MAX_ITER) {
            const errorCode = "Out of iterations while calculating airwires";
            log?.(errorCode);
        }
        // Somebody might argue that this algorithm is not actually Kruskal but instead Prim, since it always
        // grows towards the neighboring nodes. I have an explaination for why that is actually not true,
        // but this comment is not large enough to contain it.
    });
    return { airwiresGraphIds, airwiresNodeIds, airwiresPositions, endpointsPerNet, airwireEdgeOpacityIndices };
}
export function getTypedBakedNodeFromMap(pcbLayoutNodes, nodeUid, type) {
    const node = pcbLayoutNodes[nodeUid];
    if (node?.type === type) {
        return node;
    }
    else {
        return undefined;
    }
}
//# sourceMappingURL=calculateAirwires.js.map